/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Edit one activity's backend-projected visibility and teacher shares.
 * @state-owner TanStack Query plus draft form state
 * @allowed-imports same-module API; app environment; shared modal/loading
 * @forbidden client-side membership inference; optimistic authorization
 * @public-api activity editor
 * @side-effects revision-aware visibility and share mutations
 * @tests WorkspaceAccessDialog.test.tsx
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { ModalDocument, SectionLabel } from '../../../shared/components/Modal';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import { updateWorkspaceActivityAccess, workspaceAccess } from '../api/workspace-access';

export function WorkspaceAccessDialog({
  activityId,
  organizationId,
}: {
  activityId: string;
  organizationId: string;
}) {
  const t = useTranslator();
  const environment = useWorkspaceEnvironment();
  const queryClient = useQueryClient();
  const { closeModal } = useOverlay();
  const queryKey = ['workspace-access', organizationId] as const;
  const access = useQuery({
    queryKey,
    queryFn: () => workspaceAccess(environment.apiClient, organizationId),
    retry: false,
  });
  const current = access.data?.activities.find((item) => item.activityId === activityId);
  const [visibility, setVisibility] = useState<'private' | 'organization'>('private');
  const [teacherUserIds, setTeacherUserIds] = useState<string[]>([]);
  useEffect(() => {
    if (!current) return;
    setVisibility(current.visibility);
    setTeacherUserIds(current.teacherUserIds);
  }, [current]);
  const save = useMutation({
    mutationFn: async () => {
      if (!current) throw new Error('Activity access is unavailable');
      return updateWorkspaceActivityAccess(environment.apiClient, current, {
        visibility,
        teacherUserIds,
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(queryKey, (state: typeof access.data) =>
        state
          ? {
              ...state,
              activities: state.activities.map((item) =>
                item.activityId === result.activityId ? result : item,
              ),
            }
          : state,
      );
      closeModal();
    },
  });
  const toggleTeacher = (userId: string) =>
    setTeacherUserIds((currentIds) =>
      currentIds.includes(userId)
        ? currentIds.filter((id) => id !== userId)
        : [...currentIds, userId],
    );

  return (
    <ModalDocument
      maxWidth={560}
      subtitle={t('權限以目前機構與伺服器回傳的協作者為準。')}
      title={t('分享與可見性')}
      footer={
        <>
          <div className="sp" />
          <button className="bt" type="button" onClick={closeModal}>
            {t('取消')}
          </button>
          <button
            className="bt pri"
            disabled={!current || save.isPending}
            type="button"
            onClick={() => save.mutate()}
          >
            {save.isPending ? t('儲存中…') : t('儲存權限')}
          </button>
        </>
      }
    >
      {access.isPending ? <VispoLoading label={t('正在載入工作區權限')} /> : null}
      {access.isError || (access.data && !current) ? (
        <div className="eqbox" role="alert">
          {t('無法載入這份教材的權限。')}
        </div>
      ) : null}
      {current ? (
        <>
          <SectionLabel first>{t('可見範圍')}</SectionLabel>
          <label className="access-choice">
            <input
              aria-label={t('只有我與指定老師')}
              checked={visibility === 'private'}
              name="visibility"
              onChange={() => setVisibility('private')}
              type="radio"
            />
            <span>
              <b>{t('只有我與指定老師')}</b>
              <small>{t('不會出現在機構共用教材。')}</small>
            </span>
          </label>
          <label className="access-choice">
            <input
              aria-label={t('整個機構可使用')}
              checked={visibility === 'organization'}
              name="visibility"
              onChange={() => setVisibility('organization')}
              type="radio"
            />
            <span>
              <b>{t('整個機構可使用')}</b>
              <small>{t('機構內老師可在共用教材找到它。')}</small>
            </span>
          </label>
          <SectionLabel>{t('指定協作老師')}</SectionLabel>
          {access.data?.recipients.length ? (
            access.data.recipients.map((recipient) => (
              <label className="access-recipient" key={recipient.userId}>
                <input
                  aria-label={recipient.displayName}
                  checked={teacherUserIds.includes(recipient.userId)}
                  onChange={() => toggleTeacher(recipient.userId)}
                  type="checkbox"
                />
                <span>{recipient.displayName}</span>
              </label>
            ))
          ) : (
            <p className="vmut">{t('目前沒有可指定的其他老師。')}</p>
          )}
          {save.isError ? (
            <div className="eqbox" role="alert">
              {t('權限已被其他操作更新，請重新開啟後再試。')}
            </div>
          ) : null}
        </>
      ) : null}
    </ModalDocument>
  );
}
